import React, { useState } from 'react';
import { 
  Zap, 
  ArrowRight, 
  ShieldCheck, 
  CheckCircle2, 
  Lock,
  Globe,
  ArrowUp,
  Check,
  Send,
  ExternalLink,
  Sparkles,
  Server
} from 'lucide-react';
import { ScaleBulkLogo } from '../brand/ScaleBulkLogo';

interface PublicFooterProps {
  onNavigate: (path: string) => void;
  onOpenAuth: (mode: 'login' | 'register') => void;
}

export const PublicFooter: React.FC<PublicFooterProps> = ({ onNavigate, onOpenAuth }) => {
  const currentYear = new Date().getFullYear();
  const [emailInput, setEmailInput] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleLink = (path: string) => {
    onNavigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput || !emailInput.includes('@')) return;
    setIsSubscribed(true);
    setTimeout(() => {
      setEmailInput('');
      setIsSubscribed(false);
    }, 4000);
  };

  const footerLinks = {
    product: [
      { label: 'One-Click Publishing', path: '/features/one-click-publishing', badge: 'Core' },
      { label: 'AI Content Studio', path: '/features/ai-content-studio' },
      { label: 'Smart Scheduling', path: '/features/smart-scheduling' },
      { label: 'Multi-Channel Analytics', path: '/features/multi-channel-analytics' },
      { label: 'Brand Memory Profiles', path: '/features/brand-memory-profiles' },
      { label: 'Media Cloud & Drive Sync', path: '/features/media-cloud-drive-sync' },
      { label: 'Developer MCP API v2', path: '/developers/mcp-api-v2', badge: 'New' },
      { label: 'Transparent Pricing Plans', path: '/pricing' }
    ],
    solutions: [
      { label: 'Marketing & Digital Agencies', path: '/solutions/marketing-digital-agencies' },
      { label: 'Content Creators & Influencers', path: '/solutions/content-creators-influencers' },
      { label: 'B2B & Enterprise Teams', path: '/solutions/b2b-enterprise-teams' },
      { label: 'Client Workspace Isolation', path: '/solutions/client-workspace-isolation' },
      { label: 'Agency Client Workspaces', path: '/solutions/agency-client-workspaces' },
      { label: 'Small Business & D2C Brands', path: '/solutions/small-business' },
      { label: 'Multi-Network Repurposing', path: '/how-it-works' }
    ],
    whatsapp: [
      { label: 'WhatsApp Suite Hub', path: '/whatsapp', badge: 'Suite' },
      { label: 'Shared Team Inbox', path: '/whatsapp/shared-inbox', badge: 'Hot' },
      { label: 'Bulk Campaign Messaging', path: '/whatsapp/bulk-messaging' },
      { label: 'Multi-Agent Smart Routing', path: '/whatsapp/multi-agent' },
      { label: 'No-Code Automation Flows', path: '/whatsapp/automation' },
      { label: 'Visual Lead Management', path: '/whatsapp/lead-management' },
      { label: 'WhatsApp Business API', path: '/whatsapp/business-api' },
      { label: 'Two-Way CRM Integration', path: '/whatsapp/crm-integration' },
      { label: 'Marketing Analytics & Telemetry', path: '/whatsapp/marketing-analytics' },
      { label: 'Enterprise WhatsApp CRM', path: '/whatsapp/crm', badge: 'Hub' }
    ],
    integrations: [
      { label: 'Instagram (Graph API)', path: '/integrations/instagram' },
      { label: 'Facebook (Pages API)', path: '/integrations/facebook' },
      { label: 'LinkedIn (Posts API)', path: '/integrations/linkedin' },
      { label: 'X / Twitter (API v2)', path: '/integrations/x' },
      { label: 'TikTok (Content Posting)', path: '/integrations/tiktok' },
      { label: 'YouTube (Data API)', path: '/integrations/youtube' },
      { label: 'Pinterest (API v5)', path: '/integrations/pinterest' },
      { label: 'HubSpot & Salesforce Sync', path: '/whatsapp/crm-integration' }
    ],
    resources: [
      { label: 'Blog & Social Playbooks', path: '/blog' },
      { label: 'How Scale Bulk Works', path: '/how-it-works' },
      { label: 'All Supported Integrations', path: '/integrations' },
      { label: 'Frequently Asked Questions', path: '/faq' },
      { label: 'About Scale Bulk', path: '/about' },
      { label: 'Contact Sales & Support', path: '/contact' },
      { label: 'System Reliability & SLA', path: '/about#engineering' }
    ]
  };

  const officialSocialLinks = [
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/scalebulk/',
      color: '#1877F2',
      svg: (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      )
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/scalebulk/',
      color: '#E1306C',
      svg: (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
        </svg>
      )
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/company/scale-bulk',
      color: '#0A66C2',
      svg: (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
        </svg>
      )
    },
    {
      name: 'YouTube',
      url: 'https://www.youtube.com/@scalebulk',
      color: '#FF0000',
      svg: (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      )
    }
  ];

  return (
    <footer 
      style={{ 
        background: 'var(--bg-secondary)', 
        borderTop: '1px solid var(--border-color)', 
        color: 'var(--text-primary)', 
        paddingTop: '64px', 
        paddingBottom: '36px',
        position: 'relative',
        overflow: 'hidden'
      }} 
      aria-label="Scale Bulk Footer"
    >
      <style>{`
        .footer-main-grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr 1fr 1.15fr 1fr 1fr;
          gap: 36px;
          margin-bottom: 56px;
        }
        @media (max-width: 1200px) {
          .footer-main-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 32px;
          }
          .footer-brand-col {
            grid-column: span 3;
            max-width: 600px;
          }
        }
        @media (max-width: 768px) {
          .footer-main-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 28px;
          }
          .footer-brand-col {
            grid-column: span 2;
          }
        }
        @media (max-width: 480px) {
          .footer-main-grid {
            grid-template-columns: 1fr;
            gap: 28px;
          }
          .footer-brand-col {
            grid-column: span 1;
          }
        }
        .footer-nav-link {
          text-decoration: none;
          font-size: 0.86rem;
          color: var(--text-secondary);
          transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          line-height: 1.4;
        }
        .footer-nav-link:hover {
          color: var(--primary);
          transform: translateX(2px);
        }
        .footer-nav-link.wa-link:hover {
          color: #25D366;
        }
        .footer-social-btn {
          width: 34px;
          height: 34px;
          border-radius: 9px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          transition: all 0.2s ease;
          text-decoration: none;
          cursor: pointer;
        }
        .footer-social-btn:hover {
          border-color: var(--primary);
          color: var(--text-primary);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
        }
      `}</style>

      <div style={{ width: '100%', maxWidth: '1280px', margin: '0 auto', padding: '0 clamp(16px, 4vw, 24px)', boxSizing: 'border-box' }}>
        
        {/* Top Trust Header Pill Strip */}
        <div style={{
          padding: '14px 20px',
          borderRadius: 14,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 48
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 10px #22c55e' }} />
            <span>Official Platform Integration Status: 7 Social Networks &amp; WhatsApp Cloud APIs Monitored</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lock size={13} color="#22c55e" />
              <span>AES-256-GCM Vault</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ShieldCheck size={14} color="#3b82f6" />
              <span>Official OAuth 2.0 PKCE</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Server size={13} color="#8b5cf6" />
              <span>Production-Ready Infrastructure</span>
            </span>
          </div>
        </div>

        {/* 6-Column Main Navigation Grid */}
        <div className="footer-main-grid">
          
          {/* Column 1: Brand Info & Newsletter */}
          <div className="footer-brand-col">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); handleLink('/'); }}
              style={{ display: 'inline-block', marginBottom: '16px', textDecoration: 'none', color: 'inherit' }}
              aria-label="Scale Bulk Homepage"
            >
              <ScaleBulkLogo size="sm" showTagline={false} />
            </a>

            <div style={{ fontSize: '0.98rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>
              Create Once. Publish Everywhere.
            </div>

            <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '20px' }}>
              Scale Bulk is the unified social media management and multi-channel publishing platform for high-velocity teams. Connect 7 networks, schedule multi-channel posts, and convert organic reach into revenue from one cockpit.
            </p>

            {/* Newsletter Input Box */}
            <div style={{ marginBottom: '22px' }}>
              <div style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Get social media strategy insights &amp; Scale Bulk updates
              </div>
              <form onSubmit={handleNewsletterSubmit} style={{ display: 'flex', gap: 6 }}>
                <input
                  type="email"
                  placeholder="Enter work email..."
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    fontSize: '0.82rem',
                    outline: 'none'
                  }}
                />
                <button
                  type="submit"
                  disabled={isSubscribed}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: isSubscribed ? '#16a34a' : '#2563eb',
                    color: '#FFFFFF',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSubscribed ? <Check size={14} /> : <Send size={13} />}
                  <span>{isSubscribed ? 'Subscribed' : 'Join'}</span>
                </button>
              </form>
            </div>

            {/* Supported Platforms */}
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 12 }}>
                Supported Platforms
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {officialSocialLinks.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-nav-link"
                    aria-label={`ScaleBulk on ${s.name}`}
                  >
                    <span style={{ color: s.color, display: 'inline-flex', alignItems: 'center' }}>
                      {s.svg}
                    </span>
                    <span>{s.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Column 2: Product & Platform */}
          <div>
            <h3 style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-primary)', marginBottom: '16px' }}>
              Product
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '11px' }}>
              {footerLinks.product.map(link => (
                <li key={link.path}>
                  <a
                    href={link.path}
                    onClick={(e) => { e.preventDefault(); handleLink(link.path); }}
                    className="footer-nav-link"
                  >
                    <span>{link.label}</span>
                    {link.badge && (
                      <span style={{ fontSize: '0.64rem', padding: '1px 5px', borderRadius: 4, background: 'rgba(59, 130, 246, 0.15)', color: 'var(--primary-light, #60a5fa)', fontWeight: 700 }}>
                        {link.badge}
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Solutions */}
          <div>
            <h3 style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-primary)', marginBottom: '16px' }}>
              Solutions
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '11px' }}>
              {footerLinks.solutions.map(link => (
                <li key={link.path}>
                  <a
                    href={link.path}
                    onClick={(e) => { e.preventDefault(); handleLink(link.path); }}
                    className="footer-nav-link"
                  >
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: WhatsApp Business Suite */}
          <div>
            <h3 style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--whatsapp-text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--whatsapp-text)' }} />
              <span>WhatsApp Suite</span>
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '11px' }}>
              {footerLinks.whatsapp.map(link => (
                <li key={link.path}>
                  <a
                    href={link.path}
                    onClick={(e) => { e.preventDefault(); handleLink(link.path); }}
                    className="footer-nav-link wa-link"
                  >
                    <span>{link.label}</span>
                    {link.badge && (
                      <span style={{ fontSize: '0.64rem', padding: '1px 5px', borderRadius: 4, background: 'rgba(37, 211, 102, 0.15)', color: 'var(--whatsapp-text)', fontWeight: 700 }}>
                        {link.badge}
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Official Integrations */}
          <div>
            <h3 style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-primary)', marginBottom: '16px' }}>
              Integrations
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '11px' }}>
              {footerLinks.integrations.map(link => (
                <li key={link.path}>
                  <a
                    href={link.path}
                    onClick={(e) => { e.preventDefault(); handleLink(link.path); }}
                    className="footer-nav-link"
                  >
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 6: Resources & Regional */}
          <div>
            <h3 style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-primary)', marginBottom: '16px' }}>
              Company
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '11px' }}>
              {footerLinks.resources.map(link => (
                <li key={link.label}>
                  <a
                    href={link.path}
                    onClick={(e) => { e.preventDefault(); handleLink(link.path); }}
                    className="footer-nav-link"
                  >
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
              <li key="email-desk">
                <a
                  href="mailto:contact@scalebulk.com"
                  className="footer-nav-link"
                  style={{ color: 'var(--primary-light, #60a5fa)', fontWeight: 600 }}
                  title="Direct Email Desk"
                >
                  <span>contact@scalebulk.com</span>
                </a>
              </li>
            </ul>

            {/* Regional Editions */}
            <div style={{ marginTop: '22px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Globe size={12} aria-hidden="true" />
                <span>Regional Editions</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.75rem', lineHeight: 1.6 }}>
                <a 
                  href="/social-media-management-usa" 
                  onClick={(e) => { e.preventDefault(); handleLink('/social-media-management-usa'); }} 
                  style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
                  aria-label="ScaleBulk United States Regional Edition"
                >
                  United States
                </a>
                <span style={{ color: 'var(--border-color)' }} aria-hidden="true">•</span>
                <a 
                  href="/social-media-management-uk" 
                  onClick={(e) => { e.preventDefault(); handleLink('/social-media-management-uk'); }} 
                  style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
                  aria-label="ScaleBulk United Kingdom Regional Edition"
                >
                  United Kingdom
                </a>
                <span style={{ color: 'var(--border-color)' }} aria-hidden="true">•</span>
                <a 
                  href="/social-media-management-india" 
                  onClick={(e) => { e.preventDefault(); handleLink('/social-media-management-india'); }} 
                  style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
                  aria-label="ScaleBulk India Regional Edition"
                >
                  India
                </a>
                <span style={{ color: 'var(--border-color)' }} aria-hidden="true">•</span>
                <a 
                  href="/social-media-management-uae" 
                  onClick={(e) => { e.preventDefault(); handleLink('/social-media-management-uae'); }} 
                  style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
                  aria-label="ScaleBulk UAE Regional Edition"
                >
                  UAE
                </a>
                <span style={{ color: 'var(--border-color)' }} aria-hidden="true">•</span>
                <a 
                  href="/social-media-management-russia" 
                  onClick={(e) => { e.preventDefault(); handleLink('/social-media-management-russia'); }} 
                  style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
                  aria-label="ScaleBulk Russia Regional Edition"
                >
                  Russia
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Bar: Copyright, Compliance & Quick Links */}
        <div style={{
          borderTop: '1px solid var(--border-color)',
          paddingTop: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          fontSize: '0.82rem',
          color: 'var(--text-muted)'
        }}>
          <div>
            © {currentYear} Scale Bulk Inc. All rights reserved.
          </div>

          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            Official Developer Graph &amp; Cloud Platform Integrations · Zero unauthorized browser scraping.
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap' }}>
            <a href="/privacy-policy" onClick={(e) => { e.preventDefault(); handleLink('/privacy-policy'); }} style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.8rem' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}>Privacy Policy</a>
            <a href="/terms-and-conditions" onClick={(e) => { e.preventDefault(); handleLink('/terms-and-conditions'); }} style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.8rem' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}>Terms &amp; Conditions</a>
            <a href="/disclaimer" onClick={(e) => { e.preventDefault(); handleLink('/disclaimer'); }} style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.8rem' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}>Disclaimer</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); handleLink('/contact'); }} style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.8rem' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}>Contact</a>
            
            {/* Back to top button */}
            <button
              onClick={handleScrollTop}
              title="Back to Top"
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginLeft: 4
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--primary)';
                e.currentTarget.style.borderColor = 'var(--primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-secondary)';
                e.currentTarget.style.borderColor = 'var(--border-color)';
              }}
            >
              <ArrowUp size={14} />
            </button>
          </div>
        </div>

      </div>
    </footer>
  );
};
